<template>
  <div>
    <v-form ref="form" data-cy="podMenu-subtitles-form" class="no-selection">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <div class="pod-subtitlesform-inout d-flex justify-space-between flex-grow-1">
            <v-text-field
              v-model="inTime"
              v-bind:rules="timeInputRules"
              v-bind:placeholder="$t('routes.pod.menuSubtitles.inTime')"
              v-on:change="inoutTimeUpdated"
              block density="compact"
              variant="solo-filled" flat
              data-cy="podMenu-subtitles-inTime"
            />

            <v-text-field
              v-model="duration"
              v-bind:placeholder="$t('routes.pod.menuSubtitles.duration')"
              v-bind:rules="durationRules"
              v-on:change="durationUpdated"
              class="mx-10"
              density="compact"
              variant="solo-filled"
              flat persistent-placeholder
              data-cy="podMenu-subtitles-duration"
            />

            <v-text-field
              v-model="outTime"
              v-bind:rules="timeInputRules.concat(outTimeComparison)"
              v-bind:placeholder="$t('routes.pod.menuSubtitles.outTime')"
              v-on:change="inoutTimeUpdated"
              block density="compact"
              variant="solo-filled" flat
              data-cy="podMenu-subtitles-outTime"
            />
          </div>
        </v-card-title>

        <div class="subtitles-form-body">
          <!-- Overlap Warning Alert -->
          <v-alert
            v-if="hasOverlapWarnings"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            <div class="d-flex align-start">
              <div class="flex-grow-1">
                <div class="text-subtitle-2 font-weight-bold mb-2">
                  {{ $t('routes.pod.menuSubtitles.overlapWarningTitle', overlapWarnings.length) }}
                </div>
                <div class="overlap-details">
                  <div
                    v-for="(warning, index) in overlapWarnings"
                    v-bind:key="index"
                    class="overlap-item mb-1"
                  >
                    <v-icon size="14" class="mr-1">mdi-chevron-right</v-icon>
                    <span class="text-body-2">{{ formatOverlapMessage(warning) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-alert>

          <v-card-text>
            <v-textarea
              v-model="subtitleLine"
              class="media-list-notes-textarea mt-2"
              v-bind:error-messages="subtitleLineError"
              v-bind:placeholder="$t('routes.pod.menuSubtitles.subtitleLine')"
              rows="3"
              hide-details="auto"
              variant="solo-filled"
              required flat
              data-cy="podMenu-subtitles-addSubtitleLine"
            />
          </v-card-text>

          <v-card-actions class="d-flex justify-space-between">
            <div v-show="subtitleLine.length > 80">
              <v-tooltip location="bottom" :aria-label="$t('routes.pod.menuSubtitles.keepLengthShort')">
                <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <span style="color: #ff8282;">{{ subtitleLine.length }}</span>
                  {{ $t('routes.pod.menuSubtitles.characterShort') }}
                </span>
                </template>

                <span>{{ $t('routes.pod.menuSubtitles.keepLengthShort') }}</span>
              </v-tooltip>
            </div>
            <div v-show="subtitleLine.length <= 80">
              {{ subtitleLine.length }} {{ $t('routes.pod.menuSubtitles.characterShort') }}
            </div>

            <div v-show="characterPerSecond > 24">
              <v-tooltip location="bottom" :aria-label="$t('routes.pod.menuSubtitles.keepCSPShort')">
                <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <span style="color: #ff8282;">{{ characterPerSecond }}</span>
                  {{ $t('routes.pod.menuSubtitles.charsPerSec') }}
                </span>
                </template>

                <span>{{ $t('routes.pod.menuSubtitles.keepCSPShort') }}</span>
              </v-tooltip>
            </div>
            <div v-show="characterPerSecond <= 24">
              {{ characterPerSecond }} {{ $t('routes.pod.menuSubtitles.charsPerSec') }}
            </div>

            <div>
              <v-btn color="primary"
                     v-bind:title="this?.subtitleForEditing?.id ? $t('common.save') : $t('common.add')"
                     v-on:click.prevent="saveLine"
                     variant="flat"
                     data-cy="podMenu-subtitles-save-btn">
                <v-icon v-if="this?.subtitleForEditing?.id">mdi-content-save</v-icon>
                <v-icon v-else>mdi-plus-thick</v-icon>
              </v-btn>

              <v-btn variant="outlined" class="ml-2" v-bind:title="$t('common.cancel')"
                     v-on:click.prevent="hideForm" data-cy="podMenu-subtitles-cancel-btn"
                     elevation="0">
                <v-icon>mdi-cancel</v-icon>
              </v-btn>
            </div>
          </v-card-actions>
        </div>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { useSubtitleStore } from '@/stores/SubtitleStore.js';
import { toTimeMilliseconds } from '@/components/formatters.js';
import { toRaw } from 'vue';
import { cloneDeep } from 'lodash';

export default {
  emits: ["visibility"],
  name: "PodSubtitlesForm",

  props: {
    subtitleForEditing: {
      type: Object,
    },
    pod: {
      type: Object,
      required: true,
    },
    overlapWarnings: {
      type: Array,
      default: () => [],
    },
  },

  data: function() {
    let self = this;
    return {
      copiedCues: [],

      inTime: "00:00.00",
      outTime: "00:01.00",
      duration: '1',
      durationRules: [
        (v) => v >= 0 || this.$t('routes.pod.menuSubtitles.timeIsRequired'),
        (v) => /^\d{1,}(\.\d{1})?$/g.test(v) || this.$t('routes.pod.menuSubtitles.invalidFormat'),
      ],

      subtitleLine: "",
      subtitleLineError: "",

      timeInputRules: [
        (v) => !!v || this.$t('routes.pod.menuSubtitles.timeIsRequired'),
        (v) => /^\d{2,}:\d{2}\.\d{2}$/g.test(v) || this.$t('routes.pod.menuSubtitles.invalidFormat'),
        function(v) {
          let seconds = (v || "").split(".")[0].split(":")[1];
          if (parseInt(seconds) >= 60) return self.$t('routes.pod.menuSubtitles.invalidSeconds');
          return true;
        },
        function(v) {
          let videoDuration = (self.transcodedAsset && self.transcodedAsset.duration) ? parseFloat(self.transcodedAsset.duration) : 0;
          if (!videoDuration) return true;
          if ((self.videotimeToInt(v) / 1000) > videoDuration) return self.$t('routes.pod.menuSubtitles.incorrectTime');
          return true;
        }
      ],
    };
  },

  mounted: function() {
    this.refreshInputs(this.subtitleForEditing);

    // Clear any initial selection
    this.$nextTick(() => {
      this.clearSelection();
    });
  },

  methods: {
    clearSelection: function() {
      // Clear browser text selection
      if (window.getSelection) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }
      }

      // Remove focus from any active element
      if (document.activeElement) {
        document.activeElement.blur();
      }
    },
    videotimeToInt: function(value) {
      let minutes = value.split(":")[0];
      let seconds = value.split(".")[0].split(":")[1];
      let milliseconds = value.split(".")[1];

      milliseconds = parseInt(parseFloat("0." + milliseconds) * 1000);
      seconds = parseInt(seconds) * 1000;
      minutes = parseInt(minutes) * 60000;

      return minutes + seconds + milliseconds;
    },
    durationUpdated: async function() {
      if (this.duration === "") {
        this.duration = 0;
      }

      const validateFormResult = await this.$refs.form.validate();
      if (!validateFormResult.valid) {
        return;
      }

      let inTime = this.videotimeToInt(this.inTime);
      let duration = parseFloat(this.duration) * 1000;
      let outTime = inTime + duration;

      this.outTime = toTimeMilliseconds(outTime / 1000);
    },
    inoutTimeUpdated: function() {
      let inTime = this.videotimeToInt(this.inTime);
      let outTime = this.videotimeToInt(this.outTime);

      this.duration = this.calculateDuration({
        startTime: inTime,
        endTime: outTime,
      });
    },
    hideForm: function() {
      this.showOverlapConfirmation = false;
      this.userAcceptedOverlaps = false;
      this.$emit('visibility', false);
    },
    refreshInputs: function(item) {
      this.subtitleLine = item.text;

      this.subtitleLineError = "";

      this.duration = this.calculateDuration(item);
      this.inTime = item.startTime ? toTimeMilliseconds(item.startTime / 1000) : '00:00.00';
      this.outTime = item.endTime ? toTimeMilliseconds(item.endTime / 1000) : '00:01.00';
    },
    saveLine: async function() {
      this.subtitleLineError = "";
      const validateFormResult = await this.$refs.form.validate();
      if (!validateFormResult.valid) {
        return;
      }

      let isNewLine = false;
      if (!this.subtitleForEditing?.id) {
        isNewLine = true;
      }

      if (!this.subtitleLine) {
        this.subtitleLineError = this.$t('routes.pod.menuSubtitles.subtitleIsRequired');
        return;
      }

      const payload = {
        subtitle: {
          id: isNewLine ? null : this.subtitleForEditing.id,
          text: this.subtitleLine,
          startTime: this.videotimeToInt(this.inTime),
          endTime: this.videotimeToInt(this.outTime),
        },
      };

      const subtitleStore = useSubtitleStore();
      const subtitlesSnapshot = cloneDeep(toRaw(subtitleStore.getSubtitles));

      if (isNewLine) {
        subtitleStore.addSubtitleLine(payload);
      } else {
        subtitleStore.updateSubtitleLine(payload);
      }

      this.saveAsset(subtitlesSnapshot);
    },

    saveAsset: async function (subtitlesSnapshot) {
      let self = this;

      const subtitleStore = useSubtitleStore();
      const selectedAsset = subtitleStore.getSelectedAsset;
      const cues = subtitleStore.getTransformedSubtitles;

      const data = {
        podID: this.pod.id,
        assetID: selectedAsset.id,
        payload: {
          cues,
          allowOverlaps: true,
          autoFixStrategy: this.autoFixStrategy,
        }
      };

      const podStore = usePodStore();

      try {
        await podStore.updateSubtitleAsset(data);
        this.hideForm();
      } catch (error) {
        subtitleStore.setSubtitles(subtitlesSnapshot);

        if (error?.response?.data?.error?.message) {
          this.$showMessage(error.response.data.error.message, { type: 'error' });
        } else {
          console.log(error);
          this.$showMessage(self.$t('backend.system-error'), { type: 'error' });
        }
      }
    },
    roundTo(n, digits) {
      if (digits === undefined) {
        digits = 0;
      }

      let multiplicator = Math.pow(10, digits);
      n = parseFloat((n * multiplicator).toFixed(11));
      let test = (Math.round(n) / multiplicator);
      return +(test.toFixed(digits));
    },
    calculateDuration(item) {
      if (typeof item.startTime === 'number' && typeof item.endTime === 'number') {
        if (item.endTime > 0) {
          const duration = ((item.endTime - item.startTime) / 1000);
          const result = this.roundTo(duration, 1);
          return result > 0 ? result : 0;
        }
      }

      return 0;
    },
    formatTimeFromMilliseconds(milliseconds) {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const ms = milliseconds % 1000;

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
    },
    formatOverlapMessage(warning) {
      if (!warning) {
        return 'Overlap detected';
      }

      const overlapDurationFormatted = this.formatTimeFromMilliseconds(warning.overlapDuration);

      return this.$t('routes.pod.menuSubtitles.overlapWarningMessage', {
        p1: warning.messageParams.p1,
        p2: warning.messageParams.p2,
        p3: overlapDurationFormatted,
      });
    },
  },

  watch: {
    subtitleForEditing: function(newItem) {
      this.refreshInputs(newItem);

      // Clear selection after inputs are refreshed
      this.$nextTick(() => {
        this.clearSelection();
      });
    },
  },

  computed: {
    transcodedAsset: function() {
      if (!this.pod ||
        !this.pod.asset ||
        !this.pod.asset.web ||
        !Array.isArray(this.pod.asset.web)) {
        return null;
      }

      for(let i = 0; i < this.pod.asset.web.length; i++) {
        if (this.pod.asset.web[i].state == this.getPodStates.COMPLETE) {
          return this.pod.asset.web[i];
        }
      }

      return null;
    },
    getPodStates: function() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    outTimeComparison: function () {
      return this.videotimeToInt(this.inTime) < this.videotimeToInt(this.outTime) ||
        this.$t('routes.pod.menuSubtitles.invalidOutTime');
    },
    characterPerSecond: function() {
      let char = this.subtitleLine.length;
      let duration = parseFloat(this.duration);

      if (duration == 0) {
        return "-";
      }

      let csp = char / duration;
      return csp.toFixed(1);
    },
    hasOverlapWarnings() {
      return this.overlapWarnings && this.overlapWarnings.length > 0;
    },
  }
};
</script>

<style lang="scss" scoped>
.pod-subtitlesform-inout {
  > div {
    width: 33%;
  }

  :deep(.v-input__control) {
    max-height: 36px;
  }

  :deep(.v-input__details) {
    white-space: wrap;
    padding-inline: 0;
  }
}

.subtitles-form-body {
  border-color: #EBEBEB !important;
  margin-bottom: 8px;
  padding: 8px;
  border-radius: 1px 0 10px 10px;
  border-width: 0 1px 1px 1px;
  border-style: solid;
}

.subtitle-assets-list {
  overflow-y: auto;
  min-height: 118px;
  max-height: 200px;
}

.overlap-details {
  .overlap-item {
    display: flex;
    align-items: flex-start;
    padding: 4px 0;
  }
}

:global(.v-theme--dark .subtitle-line) {
  background-color: #2f3640;
  border-bottom: 2px solid #232b36;
  border-radius: 8px;
}

:global(.v-theme--light .subtitle-line) {
  background-color: #e6ecf1;
  border-bottom: 2px solid #FFFFFF;
}
</style>
